class TmsRecentlyViewed extends HTMLElement {
  static STORAGE_KEY = 'tms_recently_viewed_v1';
  static MAX_TRACKED = 12;
  static FETCH_TIMEOUT_MS = 5000;

  connectedCallback() {
    this.currentHandle = this.dataset.currentHandle || '';
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.currency = this.dataset.currency || 'AUD';
    this.locale = this.dataset.locale || 'en-AU';
    this.listEl = this.querySelector('[data-recently-viewed-list]');
    this.cardTemplate = this.querySelector('[data-recently-viewed-card]');

    this.trackCurrent();
    this.render();
  }

  readStorage() {
    try {
      const raw = localStorage.getItem(TmsRecentlyViewed.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  }

  writeStorage(handles) {
    try {
      localStorage.setItem(TmsRecentlyViewed.STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      // Quota exceeded or storage disabled — silently ignore
    }
  }

  trackCurrent() {
    if (!this.currentHandle) return;
    const existing = this.readStorage();
    const next = [this.currentHandle, ...existing.filter((h) => h !== this.currentHandle)];
    this.writeStorage(next.slice(0, TmsRecentlyViewed.MAX_TRACKED));
  }

  async render() {
    const handles = this.readStorage()
      .filter((h) => h !== this.currentHandle)
      .slice(0, this.limit);

    if (handles.length === 0) return;

    const products = await Promise.all(handles.map((h) => this.fetchProduct(h)));
    const cards = products.filter(Boolean);

    if (cards.length === 0) return;

    cards.forEach((product) => this.appendCard(product));
    this.removeAttribute('hidden');
  }

  async fetchProduct(handle) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TmsRecentlyViewed.FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  appendCard(product) {
    const node = this.cardTemplate.content.firstElementChild.cloneNode(true);

    const link = node.querySelector('[data-card-link]');
    link.href = product.url;
    link.setAttribute('aria-label', product.title);

    const imageEl = node.querySelector('[data-card-image]');
    if (product.featured_image) {
      const img = document.createElement('img');
      img.src = this.resizeImage(product.featured_image, 400);
      img.loading = 'lazy';
      img.alt = product.title;
      img.width = 400;
      img.height = 400;
      imageEl.appendChild(img);
    } else {
      imageEl.classList.add('tms-recently-viewed__image--placeholder');
    }

    node.querySelector('[data-card-title]').textContent = product.title;
    node.querySelector('[data-card-price]').textContent = this.formatMoney(product.price);

    this.listEl.appendChild(node);
  }

  resizeImage(url, width) {
    if (!url) return '';
    return url.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?|$)/i, `_${width}x$1$2`);
  }

  formatMoney(cents) {
    const value = (parseInt(cents, 10) || 0) / 100;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2
      }).format(value);
    } catch (e) {
      return `$${value.toFixed(2)}`;
    }
  }
}

if (!customElements.get('tms-recently-viewed')) {
  customElements.define('tms-recently-viewed', TmsRecentlyViewed);
}
